import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import { SocialSidebar } from '@/components/feature/SocialSidebar';
import { useTranslation } from 'react-i18next';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  id: string;
  icon: string;
  label: string;
  color: string;
  borderColor: string;
  iconColor: string;
  items: FAQItem[];
}

const faqCategories: FAQCategory[] = [
  {
    id: 'algemeen',
    icon: 'ri-question-line',
    label: 'Algemeen',
    color: 'from-violet-600/15 to-fuchsia-600/15',
    borderColor: 'border-violet-500/25',
    iconColor: 'text-violet-400',
    items: [
      {
        question: 'Wat is ZpiritualZ?',
        answer: 'ZpiritualZ is een spirituele webshop gespecialiseerd in beschermingsproducten gebaseerd op eeuwenoude tradities. Wij bieden twee hoofdseries: de Blue Protect Serie (op basis van indigofera) en de Evil Energy Destroy Serie (op basis van hing/asafoetida). Alle producten zijn 100% puur natuurlijk, zonder synthetische toevoegingen.',
      },
      {
        question: 'Zijn jullie producten veilig voor dagelijks gebruik?',
        answer: 'Ja, al onze producten zijn samengesteld uit puur natuurlijke ingrediënten zonder synthetische toevoegingen, vulstoffen of chemische derivaten. Ze zijn geschikt voor dagelijks gebruik. Voor baby\'s raden we de speciaal geformuleerde Baby Hing Pasta aan, die zachter is voor de gevoelige babyhuid. Bij twijfel over allergieën raden we aan eerst een kleine hoeveelheid te testen.',
      },
      {
        question: 'Hoe snel wordt mijn bestelling geleverd?',
        answer: 'Bestellingen worden binnen 1-2 werkdagen verwerkt en verzonden. Levering binnen Nederland duurt gemiddeld 1-3 werkdagen. Internationale bestellingen kunnen 5-10 werkdagen duren afhankelijk van het land. Digitale producten zoals Blueprints zijn direct na betaling beschikbaar via de bevestigingsmail.',
      },
      {
        question: 'Kan ik een bestelling retourneren?',
        answer: 'Fysieke producten kunnen binnen 14 dagen na ontvangst worden geretourneerd, mits ongeopend en in originele verpakking. Digitale producten (Blueprints) zijn vanwege hun aard niet retourneerbaar. Neem contact op via info@zpiritualz.com voor retourinstructies.',
      },
      {
        question: 'Zijn jullie producten ook geschikt voor mensen zonder spirituele achtergrond?',
        answer: 'Absoluut. Onze producten werken op basis van krachtige natuurlijke ingrediënten die al eeuwenlang worden gebruikt in traditionele beschermingspraktijken. Je hoeft geen diepgaande spirituele kennis te hebben om de producten te gebruiken — de bijgevoegde gebruiksaanwijzingen begeleiden je stap voor stap.',
      },
    ],
  },
  {
    id: 'blue-protect',
    icon: 'ri-shield-star-line',
    label: 'Blue Protect Serie',
    color: 'from-violet-600/15 to-indigo-600/15',
    borderColor: 'border-violet-500/25',
    iconColor: 'text-violet-400',
    items: [
      {
        question: 'Wat is de Blue Protect Serie?',
        answer: 'De Blue Protect Serie is een lijn van beschermingsproducten op basis van puur indigofera-extract — het blauwe poeder dat al generaties lang wordt gebruikt in sjamaanse reinigingsrituelen. De serie bestaat uit zeep, beschermingsballen, poeder en spray. Elk product is gericht op aura-reiniging en spirituele bescherming.',
      },
      {
        question: 'Waarvoor gebruik ik de Blue Protect Balls?',
        answer: 'De Blue Protect Balls hebben twee toepassingen: (1) Los een bal op in een warm bad voor een diepe energetische reiniging van het lichaam — laat 20 minuten weken. (2) Zet een bal in een glas water, voeg een scheutje Evil Energy Destroy Cologne toe en plaats het glas in een hoek van de ruimte die beschermd moet worden. Vervang wekelijks voor optimale bescherming.',
      },
      {
        question: 'Hoe gebruik ik het Blue Protect Poeder?',
        answer: 'Het Blue Protect Poeder heeft meerdere toepassingen: als drempelpoeder (strooi een dunne lijn voor deuren en vensters), als badpoeder (voeg 2-3 eetlepels toe aan een warm bad) of in rituelen als onderdeel van een reinigingscirkel. Bewaar het poeder op een droge, donkere plek.',
      },
      {
        question: 'Kan ik de Blue Protect Spray combineren met andere producten?',
        answer: 'Ja, de Blue Protect Spray werkt uitstekend in combinatie met andere producten uit de serie. Voor maximale bescherming kun je de spray gebruiken na het bad met de zeep of ballen. De spray is ook ideaal voor onderweg — spray rondom je lichaam na contact met drukke omgevingen of mensen die negatieve energie meebrengen.',
      },
      {
        question: 'Waarom is het extract blauw?',
        answer: 'De blauwe kleur is 100% afkomstig van puur indigofera-extract — precies zoals onze voorouders dat al eeuwenlang gebruikten in sjamaanse beschermingsrituelen. Er worden geen synthetische kleurstoffen of chemische toevoegingen gebruikt. De intensiteit van de blauwe kleur is een directe indicator van de zuiverheid van het extract.',
      },
    ],
  },
  {
    id: 'evil-energy',
    icon: 'ri-shield-flash-line',
    label: 'Evil Energy Destroy Serie',
    color: 'from-red-900/20 to-orange-900/15',
    borderColor: 'border-red-500/25',
    iconColor: 'text-red-400',
    items: [
      {
        question: 'Wat is het verschil tussen de Blue Protect en Evil Energy Destroy Serie?',
        answer: 'De Blue Protect Serie is gericht op dagelijkse aura-reiniging en bescherming — ideaal als preventieve bescherming. De Evil Energy Destroy Serie is zwaarder en gericht op het actief vernietigen van kwade energieën, het boze oog en spirituele aanvallen. Gebruik de Evil Energy Destroy Serie wanneer gewone bescherming niet meer volstaat of bij herhaalblootstelling aan negatieve energieën.',
      },
      {
        question: 'Hoe werkt het badritueel met de Evil Energy Destroy producten?',
        answer: 'Het volledige badritueel werkt als volgt: Vul het bad met warm water en voeg de Evil Energy Destroy Cologne toe. Was je lichaam met de Evil Energy Destroy Zeep en laat het mengsel over je gehele lichaam stromen. Vang het water dat over je lichaam is gegaan op in een lege fles — dit water heeft de negatieve energieën opgenomen. Sluit de fles goed af, leg er een 5 cent munt in en gooi de fles achterover (zonder om te kijken) in de zee. Zo worden de kwade energieën definitief weggedragen.',
      },
      {
        question: 'Waarvoor gebruik ik de Evil Energy Destroy Cologne?',
        answer: 'De Evil Energy Destroy Cologne heeft twee hoofdtoepassingen: (1) Als badtoevoeging — voeg een flinke scheut toe aan warm badwater voor een diepe energetische reiniging. (2) Als beschermend dweelmiddel — voeg een kleine hoeveelheid toe aan het dweelwater en dweil het hele huis van achter naar voor, eindigend bij de voordeur. Door het huis te dweilen met de cologne wordt het hele huis van binnenuit beschermd.',
      },
      {
        question: 'Hoe gebruik ik het Hing Poeder als wierook?',
        answer: 'Doe een klein beetje poeder op gloeiende houtskool of in een wierookbrander. Loop door alle vertrekken terwijl de rook de ruimte vult. Begin bij de voordeur en beweeg door het huis met de klok mee. Sluit het ritueel af bij de voordeur. Zorg voor voldoende ventilatie. De scherpe, doordringende geur van hing is al eeuwenlang bekend om zijn vermogen om kwade geesten en negatieve energieën te verdrijven.',
      },
      {
        question: 'Is de Baby Hing Pasta veilig voor pasgeborenen?',
        answer: 'Ja, de Baby Hing Pasta is speciaal geformuleerd voor de gevoelige huid van baby\'s. De pasta bevat zachte natuurlijke ingrediënten: puur hing-extract, sheaboter, kokosolie en bijenwas — zonder synthetische toevoegingen of irriterende stoffen. Gebruik een kleine hoeveelheid (ter grootte van een erwt) in de haren of onder het kussen. Gebruik niet in het gezicht of op open huid.',
      },
      {
        question: 'Hoe vaak moet ik de Evil Energy Destroy producten gebruiken?',
        answer: 'Voor dagelijkse bescherming is de zeep en/of spray ideaal voor dagelijks gebruik. Het volledige badritueel met cologne wordt aanbevolen 1-3 keer per week, of vaker bij zware spirituele aanvallen. Het branden van hing poeder als wierook doe je na bezoek van veel mensen of bij een zware sfeer in huis. Luister naar je intuïtie — gebruik de producten wanneer je voelt dat het nodig is.',
      },
    ],
  },
  {
    id: 'destroy-kit',
    icon: 'ri-fire-line',
    label: 'Evil Energy Destroy Kit',
    color: 'from-orange-900/20 to-red-900/15',
    borderColor: 'border-orange-500/25',
    iconColor: 'text-orange-400',
    items: [
      {
        question: 'Voor wie is de Evil Energy Destroy Kit bedoeld?',
        answer: 'De Evil Energy Destroy Kit is het zwaarste wapen in het ZpiritualZ-arsenaal, speciaal samengesteld voor drie situaties: (1) Baby- en kinderkamers waarbij het kind herhaaldelijk schrikt, huilt of slecht slaapt. (2) Het betreden van een nieuwe woning of zaak met een negatief verleden. (3) Volwassenen die een aanwezigheid van een entiteit waarnemen. Wanneer gewone reiniging niet meer volstaat, is de Destroy Kit de oplossing.',
      },
      {
        question: 'Wat zit er in de Evil Energy Destroy Kit?',
        answer: 'De kit bevat alles wat je nodig hebt voor het volledige destructieritueel: een exclusieve Blueprint met stap-voor-stap instructies, een unieke kruidenwierookmix (salie-extracten, ghandak, pepper, hing en lobhan), een abalone schelp, een uilenveer, brandkool en een witte kaars. Alles is 100% puur natuurlijk, zonder synthetische toevoegingen.',
      },
      {
        question: 'Hoe gebruik ik de Blueprint die bij de kit zit?',
        answer: 'De Blueprint begeleidt je stap voor stap door het volledige ritueel. Begin altijd met de voorbereiding: reinig jezelf met de zeep en zet je intentie neer met de witte kaars. Steek het brandkool aan in de abaloneschelp, voeg de kruidenmix toe en beweeg de rook met de uilenveer door de ruimte. Sluit het ritueel af zoals beschreven in de Blueprint. Volg de stappen nauwkeurig voor het beste resultaat.',
      },
      {
        question: 'Kan ik de kit ook gebruiken als preventieve maatregel?',
        answer: 'De kit is primair ontworpen voor zware gevallen waarbij gewone bescherming niet meer volstaat. Voor preventieve bescherming zijn de losse producten uit de Evil Energy Destroy Serie of Blue Protect Serie beter geschikt en kostenefficiënter. Gebruik de kit wanneer je echt een diep reinigingsritueel nodig hebt.',
      },
    ],
  },
  {
    id: 'blueprints',
    icon: 'ri-file-text-line',
    label: 'Ritueel Blueprints',
    color: 'from-fuchsia-900/20 to-violet-900/15',
    borderColor: 'border-fuchsia-500/25',
    iconColor: 'text-fuchsia-400',
    items: [
      {
        question: 'Wat zijn de Ritueel Blueprints?',
        answer: 'De Ritueel Blueprints zijn digitale PDF-gidsen met gedetailleerde stap-voor-stap instructies voor specifieke reinigings- en beschermingsrituelen. Ze zijn gebaseerd op eeuwenoude tradities en bevatten exacte hoeveelheden, timing, intenties en aanvullende tips. Na aankoop ontvang je de Blueprint direct per email en kun je hem altijd opnieuw downloaden via "Mijn Bestellingen".',
      },
      {
        question: 'Hoe download ik mijn Blueprint na aankoop?',
        answer: 'Na een succesvolle betaling ontvang je direct een bevestigingsmail met een downloadlink voor je Blueprint. Je kunt de Blueprint ook altijd opnieuw downloaden via de pagina "Mijn Bestellingen" — voer je emailadres in, zoek je bestelling op en klik op de downloadknop. De Blueprint is in PDF-formaat en kan op elk apparaat worden geopend.',
      },
      {
        question: 'Welke Blueprints zijn er beschikbaar?',
        answer: 'Momenteel zijn er drie Blueprints beschikbaar: (1) Evil Energy Destroy Reinigingsbad Blueprint — complete gids voor het badritueel. (2) Evil Energy Destroy Baby Hing Pasta Blueprint — beschermingsgids speciaal voor baby\'s. (3) Reinigingsrook Evil Energy Destroy Hing Poeder Blueprint — gids voor het wierookritueel. Nieuwe Blueprints worden regelmatig toegevoegd.',
      },
      {
        question: 'Kan ik een Blueprint meerdere keren downloaden?',
        answer: 'Ja, je hebt levenslange toegang tot je gekochte Blueprints. Je kunt ze altijd opnieuw downloaden via "Mijn Bestellingen" op de website. Sla de PDF ook lokaal op je apparaat op als extra backup.',
      },
    ],
  },
  {
    id: 'consultaties',
    icon: 'ri-calendar-line',
    label: 'Consultaties',
    color: 'from-emerald-900/20 to-teal-900/15',
    borderColor: 'border-emerald-500/25',
    iconColor: 'text-emerald-400',
    items: [
      {
        question: 'Wat houdt een spirituele consultatie in?',
        answer: 'Een spirituele consultatie bij ZpiritualZ is een persoonlijk gesprek waarbij we samen kijken naar jouw specifieke situatie. We bespreken welke energieën je ervaart, wat de mogelijke oorzaken zijn en welke producten en rituelen het beste bij jouw situatie passen. Consultaties zijn beschikbaar via video-call of persoonlijk.',
      },
      {
        question: 'Hoe boek ik een consultatie?',
        answer: 'Ga naar de Consultaties pagina op de website en kies een beschikbaar tijdslot. Vul je gegevens in en bevestig de boeking. Je ontvangt een bevestigingsmail met alle details. Bij vragen kun je ook direct contact opnemen via info@zpiritualz.com of WhatsApp.',
      },
      {
        question: 'Hoe lang duurt een consultatie?',
        answer: 'Een standaard consultatie duurt 45-60 minuten. In die tijd bespreken we je situatie uitgebreid en stellen we een persoonlijk beschermingsplan op. Voor complexere gevallen is een vervolggesprek mogelijk.',
      },
      {
        question: 'Kan ik ook een consultatie boeken voor iemand anders?',
        answer: 'Ja, je kunt een consultatie boeken voor een familielid of vriend. Vermeld dit bij de boeking en geef aan voor wie de consultatie is. In sommige gevallen is het ook mogelijk om op afstand te werken voor iemand die niet aanwezig kan zijn.',
      },
    ],
  },
];

const AccordionItem = ({
  item,
  isOpen,
  onToggle,
  iconColor,
}: {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
  iconColor: string;
}) => (
  <div
    className={`border border-white/8 rounded-2xl overflow-hidden transition-all duration-300 ${
      isOpen ? 'bg-white/5' : 'bg-white/[0.02] hover:bg-white/[0.04]'
    }`}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
    >
      <div className="flex items-start gap-3">
        <div className={`w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 ${iconColor}`}>
          <i className="ri-question-answer-line text-sm" />
        </div>
        <span className={`text-sm font-semibold leading-snug transition-colors ${isOpen ? 'text-white' : 'text-white/70'}`}>
          {item.question}
        </span>
      </div>
      <div className={`w-7 h-7 flex items-center justify-center rounded-full border shrink-0 transition-all duration-300 ${
        isOpen ? `border-current ${iconColor} rotate-45` : 'border-white/15 text-white/30'
      }`}>
        <i className="ri-add-line text-sm" />
      </div>
    </button>
    <div
      className={`overflow-hidden transition-all duration-300 ${
        isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
      }`}
    >
      <div className="px-6 pb-6 pl-14">
        <p className="text-white/55 text-sm leading-relaxed">{item.answer}</p>
      </div>
    </div>
  </div>
);

const FAQPage = () => {
  const { t } = useTranslation();

  useSEO({
    title: 'Veelgestelde Vragen (FAQ) | ZpiritualZ Den Haag – Spirituele Bescherming',
    description: 'Antwoorden op veelgestelde vragen over spirituele bescherming, het boze oog, Blue Protect Serie, Evil Energy Destroy producten, ritueel blueprints en consultaties bij ZpiritualZ in Den Haag.',
    canonical: 'https://www.zpiritualz.com/faq',
    keywords: 'FAQ spirituele bescherming, boze oog vragen, hing gebruik uitleg, blue protect uitleg, evil energy destroy FAQ, spirituele producten vragen',
    ogImage: 'https://public.readdy.ai/ai/img_res/68821b3b0111cbb8fe74e6ff792cfc1e.jpg',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqCategories.flatMap(cat =>
        cat.items.map(item => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        }))
      ),
    },
  });

  const [activeCategory, setActiveCategory] = useState('algemeen');
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const toggleItem = (key: string) => {
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const activeData = faqCategories.find(c => c.id === activeCategory)!;

  return (
    <div className="min-h-screen bg-[#050010]">
      <SocialSidebar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=ancient%20mystical%20library%20dark%20cosmic%20knowledge%20spiritual%20wisdom%20glowing%20manuscripts%20floating%20particles%20violet%20purple%20fuchsia%20light%20rays%20dark%20atmospheric%20cinematic%20ancient%20scrolls%20spiritual%20guidance&width=1920&height=700&seq=faqhero1&orientation=landscape"
            alt="FAQ achtergrond"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050010]/80 via-[#050010]/70 to-[#050010]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-900/30 border border-violet-500/30 rounded-full mb-6">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-question-answer-line text-violet-400 text-xs" />
            </div>
            <span className="text-violet-300 text-xs font-semibold uppercase tracking-widest">{t('faq_badge')}</span>
          </div>
          <h1 className="text-4xl lg:text-6xl font-black text-white mb-5 leading-tight">
            {t('faq_title')}{' '}
            <span className="text-transparent bg-gradient-to-r from-violet-400 via-fuchsia-400 to-violet-400 bg-clip-text">
              {t('faq_title2')}
            </span>
          </h1>
          <p className="text-white/50 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">{t('faq_sub')}</p>

          {/* Quick stats */}
          <div className="flex flex-wrap justify-center gap-8 mt-12">
            {[
              { value: `${faqCategories.reduce((a, c) => a + c.items.length, 0)}+`, label: t('faq_questions_answered') },
              { value: '6', label: t('faq_categories') },
              { value: '24/7', label: t('faq_available') },
            ].map(s => (
              <div key={s.label} className="text-center">
                <p className="text-violet-400 font-black text-2xl">{s.value}</p>
                <p className="text-white/30 text-xs mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 pb-24">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Sidebar — Category Nav */}
          <aside className="w-full lg:w-64 shrink-0">
            <div className="lg:sticky lg:top-28 bg-[#0d0020]/60 border border-violet-500/15 rounded-2xl p-4">
              <p className="text-white/30 text-xs font-semibold uppercase tracking-widest mb-4 px-2">{t('faq_categories')}</p>
              <ul className="space-y-1">
                {faqCategories.map(cat => (
                  <li key={cat.id}>
                    <button
                      onClick={() => { setActiveCategory(cat.id); setOpenItems({}); }}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer text-left ${
                        activeCategory === cat.id
                          ? `bg-gradient-to-r ${cat.color} border ${cat.borderColor} ${cat.iconColor}`
                          : 'text-white/45 hover:text-white/70 hover:bg-white/5'
                      }`}
                    >
                      <div className="w-5 h-5 flex items-center justify-center shrink-0">
                        <i className={`${cat.icon} text-sm`} />
                      </div>
                      <span className="whitespace-nowrap">{cat.label}</span>
                      <span className={`ml-auto text-xs px-1.5 py-0.5 rounded-full ${activeCategory === cat.id ? 'bg-white/15 text-white/70' : 'bg-white/5 text-white/25'}`}>
                        {cat.items.length}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* FAQ Accordion */}
          <div className="flex-1 min-w-0">
            {/* Category Header */}
            <div className={`flex items-center gap-4 p-5 bg-gradient-to-r ${activeData.color} border ${activeData.borderColor} rounded-2xl mb-6`}>
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-white/10 shrink-0`}>
                <i className={`${activeData.icon} ${activeData.iconColor} text-lg`} />
              </div>
              <div>
                <h2 className="text-white font-bold text-lg">{activeData.label}</h2>
                <p className="text-white/40 text-xs">{activeData.items.length} {t('faq_questions_in_cat_label')}</p>
              </div>
            </div>

            {/* Questions */}
            <div className="space-y-3">
              {activeData.items.map((item, i) => (
                <AccordionItem
                  key={`${activeCategory}-${i}`}
                  item={item}
                  isOpen={!!openItems[`${activeCategory}-${i}`]}
                  onToggle={() => toggleItem(`${activeCategory}-${i}`)}
                  iconColor={activeData.iconColor}
                />
              ))}
            </div>

            {/* Still have questions CTA */}
            <div className="mt-10 p-8 bg-gradient-to-r from-violet-900/25 to-fuchsia-900/20 border border-violet-500/20 rounded-3xl text-center">
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-violet-600/20 border border-violet-500/25 mx-auto mb-4">
                <i className="ri-customer-service-2-line text-violet-400 text-xl" />
              </div>
              <h3 className="text-white font-bold text-lg mb-2">{t('faq_not_found_title')}</h3>
              <p className="text-white/45 text-sm mb-6 max-w-md mx-auto leading-relaxed">{t('faq_not_found_desc')}</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-semibold text-sm rounded-full hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-mail-line text-sm" />
                  </div>
                  {t('faq_send_message')}
                </Link>
                <Link to="/consultations"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 border border-white/15 text-white/70 font-semibold text-sm rounded-full hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line text-sm" />
                  </div>
                  {t('faq_book_consult')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
